import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ELSIcon } from '@els/els-component-form-field-react';
import cx from 'classnames';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import { ELSInlineLoader } from '@els/els-ui-common-react';
import { flowRight, omit } from 'lodash';
import FocusTrap from 'focus-trap-react';
import ReactMarkdown from 'react-markdown';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import {
  ChatEntryAuthorDto,
  ChatEntryDto,
  ChatEntryEvaluationDto,
  ChatEntryStatus,
  ChatEntryTypeDto,
  ChatReferenceDto,
  FinalPromptType,
  FollowUpQuestionDto,
  PostProcessJobStatusDto,
  PostProcessJobTypeDto,
  RelatedContentDto,
  RelatedContentType,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import {
  getBookTitle,
  getBrandName,
  getCitationDisplayNumberMap,
  getCitations,
  getNextEntryEval,
  getOrderedCitations,
  getOrderedPurchasedEntitlements,
  getPostProcessJob,
  getRecommendations,
  isPostProcessJobTerminal,
  isShowEntryControls,
  shouldShowCitations,
  toastApiError
} from './ai-chat.utilities';
import { NewRelicAnalyticsAction, } from '../../models/new-relic-analytics.models';
import { ADMIN_MODAL_ID, } from './ai-chat.constants';
import {
  postChatEvaluation,
  putChatEvaluation
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.utilities';
import IconWithText from '../../components/icon-with-text/IconWithText.component';
import { AiChatUserFeedbackModal } from './AiChatUserFeedbackModal.component';
import AiChatAdminEntryModal from './AiChatAdminEntryModal.component';
import {
  copyContentToClipboard,
  formatFlashcardsForCopy,
  formatQuizQuestionsForCopy,
  MARKDOWN_REMARK_PLUGINS,
  MARKDOWN_REHYPE_PLUGINS
} from '../../utilities/common.utilities';
import { AiChatFollowUpQueries } from './AiChatFollowUpQueries.component';
import { EntrySubmitProps } from './ai-chat.models';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  fetchFlashcardsAction,
  fetchGeneratedQuizQuestionsAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import VideoThumbnail from '../../components/video-thumbnail/VideoThumbnail.component';
import RelatedContentImage from './RelatedContentImage.component';
import RelatedContentVideo from './RelatedContentVideo.component';
import { ELSTooltip } from '../../components/els.components';
import IsRender from '../../components/is-render/IsRender.component';
import PostProcessJobSection from './PostProcessJobSection.component';
import { QuizPanelMode } from '../../components/quiz-questions-panel/quiz-questions-panel.constants';
import LoadingReasoningSteps from '../../components/loading-reasoning-steps/LoadingReasoningSteps.component';
import { CitationPill } from './CitationPill.component';

const HeadingWrapper = ({ entry, children }: { entry: ChatEntryDto; children: ReactNode }) => {
  if (entry.author === ChatEntryAuthorDto.BOT) {
    return <h3 className="c-els-ai-chat__entry-author-heading">{children}</h3>;
  }
  return <h2 className="c-els-ai-chat__entry-author-heading">{children}</h2>;
};

export type AiChatEntryPropsOnly = {
  entry: ChatEntryDto;
  parentEntry: ChatEntryDto;
  onFeedbackSaveClick: (chatEval: ChatEntryEvaluationDto) => void;
  entryEvaluation: ChatEntryEvaluationDto;
  entryIndex: number;
  totalEntries: number;
  isAdminFeatureOn: boolean;
  handleSubmit: (props: EntrySubmitProps) => void;
  followUpQuestions: FollowUpQuestionDto[];
  onRelatedContentClick: (chatReference: ChatReferenceDto) => void;
  onNewRelatedContentClick?: (relatedContent: RelatedContentDto) => void;
  onUpdateChatEntryReference: (chatReference: ChatReferenceDto) => void;
  onFlashcardClick?: (entryId: number) => void;
  selectedFlashcardEntryId?: number;
  onQuizQuestionClick?: (entryId: number, mode: QuizPanelMode) => void;
  selectedQuizQuestionEntryId?: number;
  selectedQuizQuestionMode?: QuizPanelMode;
  onCitationsClick?: (entryId: number, citationIndex?: number) => void;
  isCitationsLoading: boolean;
  isFollowUpQuestionsLoading: boolean;
  isFromInitialLoad?: boolean;
  isIframe?: boolean;
}

export type AiChatEntryProps = AiChatEntryPropsOnly
  & ELSPropsFromToastService
  & ELSPropsFromModalService;

export type AiChatEntryPropsState = {
  isVideoActive: boolean;
  isRelatedContentActive: boolean;
  isRelatedContentNarrow: boolean;
  hideTooltip: boolean;
}

// Below this container width the related-content items stack one per row.
const RELATED_CONTENT_NARROW_MAX_WIDTH = 600;

// Widening must clear the threshold by this much before the layout goes back to
// multi-column, so a width resting on the boundary cannot oscillate.
const RELATED_CONTENT_NARROW_HYSTERESIS = 24;

const defaultState: AiChatEntryPropsState = {
  isVideoActive: false,
  isRelatedContentActive: false,
  isRelatedContentNarrow: false,
  hideTooltip: true,
};

export const buildCitationMessage = (message: string, visibleIndices: Set<number>): string => {
  // Tracks whether the marker we just emitted was a visible citation, so a run of
  // touching markers ([1][2][3]) can flag every pill after the first as adjacent.
  // Any non-marker character (including a space) between markers resets this, so
  // "[1] [2]" is not treated as adjacent — the space already separates them.
  let prevWasVisibleCitation = false;
  let lastMatchEnd = 0;

  return message.replace(/\[(\d+)\]/g, (match, n, offset: number) => {
    const isVisible = visibleIndices.has(parseInt(n, 10));
    const isAdjacent = isVisible && prevWasVisibleCitation && offset === lastMatchEnd;

    lastMatchEnd = offset + match.length;
    prevWasVisibleCitation = isVisible;

    if (!isVisible) {
      return match;
    }
    return `<cite data-n="${n}"${isAdjacent ? ' data-adjacent="true"' : ''}></cite>`;
  });
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const AiChatEntryComponent = (props: AiChatEntryProps) => {

  const {
    appState,
    appStateRef,
    setAppState
  } = useContext(AppContext);

  const {
    entry,
    onFeedbackSaveClick,
    entryIndex,
    totalEntries,
    isAdminFeatureOn,
    parentEntry,
  } = props;

  const isLastItem = entryIndex === totalEntries - 1;
  const isRelatedContentExpandedByDefault = entryIndex >= totalEntries - 10;
  const isTutorModeResponse = entry.author === ChatEntryAuthorDto.BOT && parentEntry?.context?.isTutorModeEnabled;

  const imagesJob = getPostProcessJob(entry, PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_IMAGES);
  const videosJob = getPostProcessJob(entry, PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_VIDEOS);
  const relatedContentJobs = [imagesJob, videosJob].filter(Boolean);
  const hasAnyRelatedContentJob = relatedContentJobs.length > 0;
  const isLoadingAnyRelatedContent = relatedContentJobs.some((job) => !isPostProcessJobTerminal(job));
  const areAllRelatedContentJobsTerminal = hasAnyRelatedContentJob && relatedContentJobs.every((job) => isPostProcessJobTerminal(job));

  // Collapse all expandable areas if this entry is from initial load and in iframe context
  const shouldCollapseAll = props.isFromInitialLoad && props.isIframe;

  const [state, setState] = useState<AiChatEntryPropsState>({
    ...defaultState,
    isRelatedContentActive: shouldCollapseAll ? false : isRelatedContentExpandedByDefault,
  });
  const relatedContentRef = useRef<HTMLDivElement>(null);

  const entitlements = getOrderedPurchasedEntitlements(appState.entitlements);

  useEffect(() => {
    const element = relatedContentRef.current;
    if (!element) {
      return undefined;
    }

    // This observer watches the same node that carries the --narrow class, and that
    // class reflows the node's children from fractional widths to width:100% (plus a
    // margin), which changes the node's own size. Writing state straight from the
    // callback therefore resizes the observed element mid-delivery, and the browser
    // reports "ResizeObserver loop completed with undelivered notifications".
    //
    // Two things keep that loop closed:
    //   1. The state write is deferred to the next animation frame, so the resize it
    //      causes lands outside the current delivery cycle.
    //   2. The threshold has a hysteresis band: once narrow, the node must grow past
    //      600 + BAND to widen again. A width parked at the boundary cannot flip the
    //      flag back and forth on successive frames.
    let frameId: number | null = null;

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width === undefined) {
        return;
      }

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        setState((prevState) => {
          const threshold = prevState.isRelatedContentNarrow
            ? RELATED_CONTENT_NARROW_MAX_WIDTH + RELATED_CONTENT_NARROW_HYSTERESIS
            : RELATED_CONTENT_NARROW_MAX_WIDTH;
          const isRelatedContentNarrow = width < threshold;
          // Returning the same reference lets React skip the re-render entirely.
          return prevState.isRelatedContentNarrow === isRelatedContentNarrow
            ? prevState
            : { ...prevState, isRelatedContentNarrow };
        });
      });
    });

    resizeObserver.observe(element);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
    };
  }, [entry.relatedContent]);

  const mergeState = (newState: Partial<AiChatEntryPropsState>) => {
    setState((prevState) => {
      return {
        ...prevState,
        ...newState,
        entryEvaluation: props.entryEvaluation
      };
    });
  };

  const getEntryClasses = (chatEntry: ChatEntryDto): string => {
    const blockClass = 'c-els-ai-chat__entry';
    if (!chatEntry || !chatEntry.author) {
      return blockClass;
    }

    const modifier = isTutorModeResponse
      ? `${blockClass}--bot-tutor`
      : `${blockClass}--${chatEntry.author.toLowerCase()}`;

    return [blockClass, modifier].join(' ');
  };

  const handleCopyClick = async () => {

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_COPY_CLICK,
        props: {
          entryId: entry.id,
          idx: entry.index
        }
      }
    });

    let contentToCopy = props.entry.message;
    let hasFlashcards = false;
    let hasQuizQuestions = false;

    // Check if this entry has flashcards
    if (
      entry.author === ChatEntryAuthorDto.BOT
      && entry.finalPromptType === FinalPromptType.FLASHCARD_ACKNOWLEDGMENT
      && getPostProcessJob(entry, PostProcessJobTypeDto.GENERATE_FLASHCARDS)?.status === PostProcessJobStatusDto.COMPLETED
    ) {
      try {
        const response = await fetchFlashcardsAction({
          appState,
          appStateRef,
          setAppState,
          botEntryId: entry.id
        });

        if (response.flashcards && response.flashcards.length > 0) {
          const formattedFlashcards = formatFlashcardsForCopy(response.flashcards);
          contentToCopy = `${props.entry.message}\n\n---\n\n${formattedFlashcards}`;
          hasFlashcards = true;
        }
      } catch (error) {
        // If fetching flashcards fails, just copy the message without them
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch flashcards for copy:', error);
      }
    } else if (
      entry.author === ChatEntryAuthorDto.BOT
      && entry.finalPromptType === FinalPromptType.QUIZ_QUESTION_ACKNOWLEDGMENT
      && getPostProcessJob(entry, PostProcessJobTypeDto.GENERATE_QUIZ_QUESTIONS)?.status === PostProcessJobStatusDto.COMPLETED
    ) {
      try {
        const quizQuestions = await fetchGeneratedQuizQuestionsAction({
          appState,
          appStateRef,
          setAppState,
          botEntryId: entry.id
        });

        if (quizQuestions && quizQuestions.length > 0) {
          const formattedQuestions = formatQuizQuestionsForCopy(quizQuestions);
          contentToCopy = `${props.entry.message}\n\n---\n\n${formattedQuestions}`;
          hasQuizQuestions = true;
        }
      } catch (error) {
        // If fetching quiz questions fails, just copy the message without them
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch quiz questions for copy:', error);
      }
    }

    copyContentToClipboard(contentToCopy);

    let toastMessage = 'Successfully copied';
    if (hasFlashcards) {
      toastMessage = 'Successfully copied (including flashcards)';
    } else if (hasQuizQuestions) {
      toastMessage = 'Successfully copied (including quiz questions)';
    }

    props.toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (
        <div>{toastMessage}</div>
      ),
      type: ELSWithToastService.types.POSITIVE
    });

    const button = document.getElementById(`copy-button-${props.entry.id}`);
    if (button) {
      button.focus();
    }
  };

  const toastSuccess = () => {
    props.toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (
        <div>
          <p>Thanks for the feedback!</p>
        </div>
      ),
      type: ELSWithToastService.types.POSITIVE
    });
  };

  const handleSubmitFeedbackClick = (
    evaluation: Partial<ChatEntryEvaluationDto>,
    isFaceClick = false,
  ): Promise<ChatEntryEvaluationDto> => {

    const {
      entryEvaluation,
    } = props;

    const dto = getNextEntryEval({
      existingEntryEval: entryEvaluation,
      userId: appState.userId,
      entryId: entry.id,
      newEntryEval: evaluation
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_FEEDBACK_SUBMIT,
        props: {
          entryId: dto.entryId,
          rating: dto.overall,
          isFaceClick,
          chatUserRole: appState.chatUserRole,
        }
      }
    });

    let promise;

    if (dto.id) {
      promise = putChatEvaluation(entry.chatId, dto);
    } else {
      promise = postChatEvaluation(entry.chatId, omit(dto, 'id'));
    }

    return promise.then((response) => {
      onFeedbackSaveClick(response);
      if (!isFaceClick) {
        toastSuccess();
      }
      return response;
    }).catch(() => {
      toastApiError(props.toastService);
    });
  };

  const handleFeedbackClick = (rating: number) => {

    const {
      modalService,
    } = props;

    handleSubmitFeedbackClick({ overall: rating }, true).then((entryEvaluation) => {
      const modalId = 'USER_FEEDBACK_MODAL';
      modalService.openCustomModal({
        modalId,
        dialogAriaLabel: 'Chat message feedback modal',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        noFocusTrap: true,
        modal: (
          <FocusTrap focusTrapOptions={{
            preventScroll: true
          }}>
            <div>
              <AiChatUserFeedbackModal
                overallRating={rating}
                entry={entry}
                handleSaveClick={(chatEval, isFaceClick) => {
                  handleSubmitFeedbackClick(chatEval, isFaceClick);
                  modalService.closeModal(modalId);
                }}
                handleCancelClick={() => {
                  trackNewRelicAction({
                    appStateRef,
                    actionProps: {
                      action: NewRelicAnalyticsAction.AI_CHAT_CANCEL_FEEDBACK_CLICK,
                      props: null
                    }
                  });
                  modalService.closeModal(modalId);
                }}
                entryEvaluation={entryEvaluation}
              />
            </div>
          </FocusTrap>
        )
      });
    });

  };

  const entryControlCssClass = 'u-els-debuttonize c-els-ai-chat__entry-control';
  const feedbackThumbCssClass = 'u-els-debuttonize c-els-ai-chat__feedback-thumb';
  const entryMainCssClass = 'c-els-ai-chat__entry-main';

  const citationsReady = shouldShowCitations(entry, props.isCitationsLoading, props.isFromInitialLoad)
    && !props.isCitationsLoading;

  const handleInlineCitationClick = useCallback((n: number) => {
    if (!citationsReady) {
      return;
    }
    const citation = (getCitations(entry) ?? []).find((reference) => reference.index === n);
    if (citation) {
      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.AI_CHAT_CITATION_CLICK,
          props: {
            type: citation.type,
            resourceType: citation.resourceType,
            citationId: citation.id,
            entryId: citation.entryId
          }
        }
      });
    }
    props.onCitationsClick?.(entry.id, n);
  }, [entry, props.onCitationsClick, citationsReady, appStateRef]);

  // Derive the clickable-pill set from the SAME source the References panel
  // uses (getOrderedCitations -> getCitationGroups), which keeps EBOOK-only.
  // Deriving straight from getCitations would make a pill clickable for a
  // non-EBOOK citation whose row the panel never renders, so the user would
  // click [n] and land on a list that omits it. Using the ordered set keeps
  // pills, the References count, and the panel rows in lockstep.
  const visibleCitationIndices = useMemo(
    () => new Set(getOrderedCitations(getCitations(entry) ?? []).map((r) => r.index)),
    [entry.references]
  );

  // Raw index -> 1-based display position, so gaps left by hidden (un-cited)
  // sources never surface to the user (e.g. [1][3][5] displays as [1][2][3]).
  const citationDisplayNumbers = useMemo(
    () => getCitationDisplayNumberMap(getCitations(entry) ?? []),
    [entry.references]
  );

  const citationComponents = useMemo(() => ({
    // ReactMarkdown's `components` prop requires this renderer to close over citationsReady/etc.
    // eslint-disable-next-line react/no-unstable-nested-components
    cite: (citeProps: React.ComponentPropsWithoutRef<'cite'> & { 'data-n'?: string; 'data-adjacent'?: string }) => {
      if (!citationsReady) return null;
      const n = parseInt(citeProps['data-n'] ?? '', 10);
      if (isNaN(n) || !visibleCitationIndices.has(n)) return null;
      const displayNumber = citationDisplayNumbers.get(n);
      if (displayNumber === undefined) return null;
      // Set by buildCitationMessage on a pill that directly touches the preceding
      // one (e.g. [1][2]) so the modifier can add a little space between them.
      const isAdjacent = citeProps['data-adjacent'] === 'true';
      return (
        <CitationPill
          displayNumber={displayNumber}
          isAdjacent={isAdjacent}
          onClick={() => handleInlineCitationClick(n)}
        />
      );
    }
  }), [citationsReady, visibleCitationIndices, citationDisplayNumbers, handleInlineCitationClick]);

  const processedMessage = useMemo(() => {
    if (!entry?.message) {
      return null;
    }
    // Only swap [n] markers for <cite> tags once citations are ready — the
    // cite renderer returns null while !citationsReady, so replacing markers
    // any earlier would make them disappear instead of staying visible as
    // plain text until citations can actually be shown.
    if (!citationsReady) {
      return entry.message;
    }
    return buildCitationMessage(entry.message, visibleCitationIndices);
  }, [entry?.message, visibleCitationIndices, citationsReady]);

  let message = null;

  if (entry?._status === ChatEntryStatus.THINKING) {
    message = <div className="u-els-padding-right-2x"><LoadingReasoningSteps/></div>;
  }

  if (!entry?.message && !entry?._status && entry?.entryType === ChatEntryTypeDto.SHELL) {
    message = (
      <div>
        Message failed to load. Please try again.
      </div>
    );
  }

  if (entry?.message) {
    message = (
      <div className="s-els-authored-content s-els-authored-content--chat-entry u-els-overflow-auto">
        <ReactMarkdown
          remarkPlugins={MARKDOWN_REMARK_PLUGINS}
          rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
          components={citationComponents}
        >
          {processedMessage}
        </ReactMarkdown>
      </div>
    );
  }

  const recommendations = getRecommendations(entry);

  const handleRelatedContentToggle = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_RELATED_CONTENT_EXPAND_TOGGLE,
        props: {
          statusAfterToggle: state.isRelatedContentActive ? 'COLLAPSED' : 'EXPANDED'
        }
      }
    });
    mergeState({ isRelatedContentActive: !state.isRelatedContentActive });
  };

  const handleOpenAdminModal = () => {
    const {
      modalService
    } = props;
    modalService.openCustomModal({
      modalId: ADMIN_MODAL_ID,
      noFocusTrap: true,
      modal: (
        <AiChatAdminEntryModal
          entry={entry}
          handleClose={() => {
            modalService.closeModal(ADMIN_MODAL_ID);
          }}
        />
      )
    });
  };

  const getIsbnFilterDisplay = (selectedIsbns: string[]): JSX.Element => {
    if (!selectedIsbns || selectedIsbns.length === 0) {
      return null;
    }

    if (selectedIsbns.length === 1) {
      const [isbn] = selectedIsbns;
      return <div key={isbn}>
        <span>From</span>
        <span className="u-els-font-family-bold-italic u-els-margin-left-1o4">{getBookTitle(isbn, appState)}</span>
      </div>;
    }

    return (
      <div>
        <span className="u-els-margin-right-1o4">From</span>
        <ELSTooltip
          id={`isbn-filter-tooltip-${entry.id}`}
          showClose={false}
          hide={state.hideTooltip}
          onOpenToolTip={() => {
            setState((prevState) => ({
              ...prevState,
              hideTooltip: false,
            }));
          }}
          onCloseToolTip={() => {
            setState((prevState) => ({
              ...prevState,
              hideTooltip: true,
            }));
          }}
          content={(
            <ul>
              {selectedIsbns.map((isbn) => (
                <li key={isbn} className="u-els-margin-bottom-1o2">
                  <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.TOP]}>
                    <FlexItem classes={["u-els-margin-right-1o2"]}>
                      &bull;
                    </FlexItem>
                    <FlexItem>
                      {getBookTitle(isbn, appState)}
                    </FlexItem>
                  </FlexLayout>
                </li>
              ))}
            </ul>
          )}>
          <button type="button" className="u-els-anchorize">
            {selectedIsbns.length} selected books
          </button>
        </ELSTooltip>
      </div>
    );
  }

  const activeRating = props.entryEvaluation ? props.entryEvaluation.overall : null;

  let activeSearchFiltersToShow = null;
  let hasActiveFilters = false;

  // Only user-selected filters are surfaced as a "From [selected books]" chip. Admin
  // search overrides are applied silently and must not be shown as if the user selected them.
  if (parentEntry?.filters?.isbns?.length > 0 && entitlements != null) {
    activeSearchFiltersToShow = parentEntry.filters.isbns;
    hasActiveFilters = true;
  }

  return (
    <div
      key={entry.id}
      className={getEntryClasses(entry)}
    >
      {
        entry.author === ChatEntryAuthorDto.BOT
        && (
          <div>
            <HeadingWrapper entry={entry}>
              <div className="c-els-ai-chat__entry-author">
                <span>{getBrandName(appState)} <span className="u-els-hide-visually"> answered: </span></span>
              </div>
            </HeadingWrapper>
            <div
              className={cx(entryMainCssClass, {
                'c-els-ai-chat__entry-main--active': false
              })}
            >
              <div className="c-els-ai-chat__entry-text">
                <FlexLayout classes={['u-els-margin-bottom-1x']} modifiers={[FlexLayoutModifier.LEFT]}>
                  <IsRender isRender={isTutorModeResponse}>
                    <FlexItem>
                      <div className="c-els-ai-chat__entry-tutor-label u-els-font-size-meta">
                        Tutor mode
                      </div>
                    </FlexItem>
                    <FlexItem isRender={hasActiveFilters}>
                      <span className="u-els-margin-left-1o2 u-els-margin-right-1o2">&bull;</span>
                    </FlexItem>
                  </IsRender>

                  <FlexItem isRender={hasActiveFilters}>
                    <div className="u-els-color-n8 u-els-font-family-italic u-els-margin-top-1o8">
                      {getIsbnFilterDisplay(activeSearchFiltersToShow)}
                    </div>
                  </FlexItem>
                </FlexLayout>

                {message}

                {
                  entry.author === ChatEntryAuthorDto.BOT && entry.finalPromptType === FinalPromptType.MCQ_RESPONSE && (
                    <div className="u-els-margin-top u-els-font-size-caption u-els-color-n8 u-els-font-family-italic">
                      <strong>Note:</strong>
                      {' '}
                      AI-generated answers to quiz questions may differ from what you see in your assessments.
                      {' '}
                      Always use your best clinical judgement before finalizing your answer.
                    </div>
                  )
                }

                <PostProcessJobSection
                  entry={entry}
                  finalPromptType={FinalPromptType.FLASHCARD_ACKNOWLEDGMENT}
                  jobType={PostProcessJobTypeDto.GENERATE_FLASHCARDS}
                  brandName={getBrandName(appState)}
                  loadingLabel="Generating flashcards"
                  errorFallback="Unable to generate flashcards"
                  actions={[{
                    label: 'View flashcards',
                    activeLabel: 'Viewing flashcards',
                    isActive: props.selectedFlashcardEntryId === entry.id,
                    onClick: (entryId) => {
                      trackNewRelicAction({
                        appStateRef,
                        actionProps: {
                          action: NewRelicAnalyticsAction.AI_CHAT_FLASHCARD_VIEW_CLICK,
                          props: {
                            entryId,
                            chatUserRole: appState.chatUserRole
                          }
                        }
                      });
                      props.onFlashcardClick?.(entryId);
                    }
                  }]}
                />

                <PostProcessJobSection
                  entry={entry}
                  finalPromptType={FinalPromptType.QUIZ_QUESTION_ACKNOWLEDGMENT}
                  jobType={PostProcessJobTypeDto.GENERATE_QUIZ_QUESTIONS}
                  brandName={getBrandName(appState)}
                  loadingLabel="Generating quiz questions"
                  errorFallback="Unable to generate quiz questions"
                  actions={[{
                    label: 'View all quiz questions',
                    activeLabel: 'Viewing all quiz questions',
                    isActive: props.selectedQuizQuestionEntryId === entry.id
                      && props.selectedQuizQuestionMode === QuizPanelMode.VIEW,
                    onClick: (entryId) => props.onQuizQuestionClick?.(entryId, QuizPanelMode.VIEW)
                  }, {
                    label: 'Take the practice quiz',
                    activeLabel: 'Taking the practice quiz',
                    isActive: props.selectedQuizQuestionEntryId === entry.id
                      && props.selectedQuizQuestionMode === QuizPanelMode.TAKE,
                    onClick: (entryId) => props.onQuizQuestionClick?.(entryId, QuizPanelMode.TAKE)
                  }]}
                />

              </div>
            </div>
          </div>
        )
      }
      {
        entry.author === ChatEntryAuthorDto.USER
        && (
          <HeadingWrapper entry={entry}>
            <div className="c-els-ai-chat__entry-author">
              <span>You <span className="u-els-hide-visually"> asked: </span></span>
            </div>
            <div
              className={cx(entryMainCssClass, {
                'c-els-ai-chat__entry-main--active': false
              })}
            >
              <div className="c-els-ai-chat__entry-text">
                {message}
              </div>
            </div>
          </HeadingWrapper>
        )
      }

      <div
        className={cx(entryMainCssClass, {
          'c-els-ai-chat__entry-main--active': false
        })}
      >
        {
          isShowEntryControls(entry) && (
            <div className="c-els-ai-chat__entry-controls">
              <FlexLayout modifiers={[
                FlexLayoutModifier.GUTTERS,
                FlexLayoutModifier.MIDDLE,
                FlexLayoutModifier.LEFT,
              ]}>
                <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                  {citationsReady && visibleCitationIndices.size > 0 && (
                    <button
                      type="button"
                      className="u-els-anchorize"
                      onClick={() => {
                        trackNewRelicAction({
                          appStateRef,
                          actionProps: {
                            action: NewRelicAnalyticsAction.AI_CHAT_CITATION_EXPAND_TOGGLE,
                            props: {
                              statusAfterToggle: 'EXPANDED'
                            }
                          }
                        });
                        props.onCitationsClick?.(entry.id);
                      }}
                    >
                      References ({visibleCitationIndices.size})
                    </button>
                  )}
                </FlexItem>
                <FlexItem>
                  <button
                    onClick={handleCopyClick}
                    id={`copy-button-${entry.id}`}
                    type="button"
                    className={cx(entryControlCssClass, 'u-els-color-secondary', 'c-els-ai-chat__entry-control--copy')}
                  >
                    <ELSIcon
                      size="1x"
                      prefix="gizmo"
                      name="duplicate-document"
                      customClass="u-els-display-block"
                    />
                    <span className="u-els-hide-visually">Copy {getBrandName(appState)}&apos;s response</span>
                  </button>
                </FlexItem>
                <FlexItem>
                  <button
                    onClick={() => {
                      handleFeedbackClick(2);
                    }}
                    type="button"
                    aria-haspopup="dialog"
                    className={cx(
                      feedbackThumbCssClass,
                      {
                        'c-els-ai-chat__feedback-thumb--active': activeRating === 2,
                      }
                    )}
                  >
                    <ELSIcon
                      size="1x"
                      prefix="gizmo"
                      name="thumbs-up"
                      customClass="c-els-ai-chat__feedback-thumb-icon"
                    />
                    <span className="u-els-hide-visually">Give positive feedback</span>
                  </button>
                </FlexItem>
                <FlexItem>
                  <button
                    onClick={() => {
                      handleFeedbackClick(0);
                    }}
                    type="button"
                    aria-haspopup="dialog"
                    className={cx(
                      feedbackThumbCssClass,
                      {
                        'c-els-ai-chat__feedback-thumb--active': activeRating === 0,
                      }
                    )}
                  >
                    <ELSIcon
                      size="1x"
                      prefix="gizmo"
                      name="thumbs-down"
                      customClass="c-els-ai-chat__feedback-thumb-icon"
                    />
                    <span className="u-els-hide-visually">Give negative feedback</span>
                  </button>
                </FlexItem>
              </FlexLayout>
            </div>
          )
        }

        {
          entry.finalPromptType !== FinalPromptType.FLASHCARD_ACKNOWLEDGMENT
            && hasAnyRelatedContentJob
            && (isLoadingAnyRelatedContent || entry.relatedContent?.length > 0) && (
            <div
              ref={relatedContentRef}
              className={cx('c-els-ai-chat__related-content', {
                'c-els-ai-chat__related-content--narrow': state.isRelatedContentNarrow
              })}
            >
              <button
                type="button"
                className="u-els-debuttonize u-els-display-block u-els-width-1o1 u-els-color-secondary"
                onClick={handleRelatedContentToggle}
                aria-expanded={state.isRelatedContentActive ? 'true' : 'false'}
              >
                <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
                  <FlexItem>
                    <ELSIcon
                      size="1x"
                      prefix="hmds"
                      name={state.isRelatedContentActive ? 'chevron-up' : 'chevron-down'}
                      customClass="u-els-display-block"
                    />
                  </FlexItem>
                  <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                    Related content
                  </FlexItem>
                </FlexLayout>
              </button>

              <IsRender isRender={state.isRelatedContentActive}>
                <div className="u-els-margin-top">
                  {isLoadingAnyRelatedContent && (
                    <div className="u-els-margin-bottom">
                      <ELSInlineLoader/>
                    </div>
                  )}
                  {areAllRelatedContentJobsTerminal && entry.relatedContent?.length > 0 && (
                    <FlexLayout
                      modifiers={[
                        FlexLayoutModifier.LEFT,
                        FlexLayoutModifier.GUTTERS_1X_1o2,
                        FlexLayoutModifier.WRAP
                      ]}>
                      {entry.relatedContent.map((relatedContent) => {
                        return (
                          <FlexItem
                            classes={['c-els-ai-chat__related-content-item', `c-els-ai-chat__related-content-item--${entry.relatedContent.length}`]}
                            id={`related-content-${relatedContent.id}`}
                            key={relatedContent.id}>
                            {relatedContent.contentType === RelatedContentType.OST && (
                              <RelatedContentVideo
                                relatedContent={relatedContent}
                                onClick={props.onNewRelatedContentClick}
                              />
                            )}
                            {relatedContent.contentType === RelatedContentType.IM && (
                              <RelatedContentImage
                                relatedContent={relatedContent}
                                onClick={props.onNewRelatedContentClick}
                              />
                            )}
                          </FlexItem>
                        );
                      })}
                    </FlexLayout>
                  )}
                </div>
              </IsRender>
            </div>
          )
        }

        {
          appState.osmosisTokenDto && recommendations?.length > 0 && (
            <div className="c-els-ai-chat__rec-list">
              <h4 className="u-els-margin-bottom-3o4">Related content</h4>
              <div>
                {
                  recommendations.map((recommendation) => {
                    return (
                      <div
                        key={recommendation.id}
                        id={`reference-content-${recommendation.id}`}
                        className="c-els-ai-chat__rec-list-item">
                        <button
                          className="u-els-debuttonize c-els-ai-chat__video-thumb"
                          onClick={() => {
                            props.onRelatedContentClick(recommendation);
                          }}>
                          <VideoThumbnail
                            reference={recommendation}
                            onUpdateChatEntryReference={props.onUpdateChatEntryReference}
                          />
                        </button>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          )
        }

        {
          isLastItem
          && (props.isFollowUpQuestionsLoading
            || (props.followUpQuestions?.length > 0 && props.followUpQuestions.some(q => q.entryId === entry.id)))
          && (
            <AiChatFollowUpQueries
              botEntry={props.entry}
              followUpQuestions={props.followUpQuestions}
              handleSubmit={props.handleSubmit}
              isFollowUpQuestionsLoading={props.isFollowUpQuestionsLoading}
              initiallyCollapsed={shouldCollapseAll}
            />
          )
        }
      </div>

      {
        isAdminFeatureOn && (
          <div className="u-els-margin-top-1o2">
            <button
              type="button"
              className="u-els-anchorize c-els-ai-chat__entry-admin-link"
              onClick={handleOpenAdminModal}
            >
              <IconWithText
                iconName="eye-visible"
                iconPrefix="hmds"
              >
                Admin view details
              </IconWithText>
            </button>
          </div>
        )
      }
    </div>
  );
};

const enhance = flowRight(
  ELSWithToastService,
  ELSWithModalService,
);

const AiChatEntry: React.ComponentType<AiChatEntryPropsOnly> = enhance(AiChatEntryComponent as React.FC<AiChatEntryProps>);

export default AiChatEntry;
